import { useState } from 'react';
import { Printer, MessageCircle, Mail, Download, X, Send } from 'lucide-react';

const ShareOptionsToolbar = ({ 
  onPrint, 
  onWhatsApp, 
  onEmail, 
  onDownload,
  onBeforeAction = null, // Called before any action with (actionType, callback)
  loading = false 
}) => {
  const [showWhatsAppInput, setShowWhatsAppInput] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Wrap action with onBeforeAction check
  const handleAction = (actionType, actionFn, needsInput = false) => {
    if (onBeforeAction) {
      onBeforeAction(actionType, (addressedTo) => {
        if (needsInput) {
          setPendingAction({ type: actionType, addressedTo });
          if (actionType === 'whatsapp') {
            setShowWhatsAppInput(true);
            setShowEmailInput(false);
          } else if (actionType === 'email') {
            setShowEmailInput(true);
            setShowWhatsAppInput(false);
          }
        } else {
          actionFn(addressedTo);
        }
      });
    } else {
      if (needsInput) {
        setPendingAction({ type: actionType });
        if (actionType === 'whatsapp') {
          setShowWhatsAppInput(true);
          setShowEmailInput(false);
        } else if (actionType === 'email') {
          setShowEmailInput(true);
          setShowWhatsAppInput(false);
        }
      } else {
        actionFn();
      }
    }
  };

  const handleWhatsAppSend = async () => {
    if (!phone.trim()) return;
    setSending(true);
    try {
      await onWhatsApp(phone, pendingAction?.addressedTo);
      setShowWhatsAppInput(false);
      setPhone('');
      setPendingAction(null);
    } catch (error) {
      console.error('WhatsApp send error:', error);
    } finally {
      setSending(false);
    }
  };

  const handleEmailSend = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await onEmail(email, pendingAction?.addressedTo);
      setShowEmailInput(false);
      setEmail('');
      setPendingAction(null);
    } catch (error) {
      console.error('Email send error:', error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 p-3 bg-secondary-offWhite rounded-lg">
        {/* Print Button */}
        <button
          onClick={() => handleAction('print', onPrint, false)}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-secondary-lightGray rounded-lg text-primary-slate hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <Printer className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">Print</span>
        </button>

        {/* WhatsApp Button */}
        <button
          onClick={() => handleAction('whatsapp', onWhatsApp, true)}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors disabled:opacity-50 ${
            showWhatsAppInput 
              ? 'bg-green-500 text-white border-green-500' 
              : 'bg-white border-secondary-lightGray text-primary-slate hover:bg-gray-50'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">WhatsApp</span>
        </button>

        {/* Email Button */}
        <button
          onClick={() => handleAction('email', onEmail, true)}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors disabled:opacity-50 ${
            showEmailInput 
              ? 'bg-blue-500 text-white border-blue-500' 
              : 'bg-white border-secondary-lightGray text-primary-slate hover:bg-gray-50'
          }`}
        >
          <Mail className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">Email</span>
        </button>

        {/* Download Button */}
        <button
          onClick={() => handleAction('download', onDownload, false)}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-accent-gold text-white rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">Download</span>
        </button>
      </div>

      {/* WhatsApp Input */}
      {showWhatsAppInput && (
        <div className="absolute z-10 left-0 right-0 mt-2 p-3 bg-white border border-secondary-lightGray rounded-lg shadow-lg animate-fade-in">
          <div className="flex items-center gap-2">
            <input
              type="tel"
              placeholder="Enter phone number (e.g., 2348012345678)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="flex-1 px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <button
              onClick={handleWhatsAppSend}
              disabled={!phone.trim() || sending}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send
            </button>
            <button
              onClick={() => setShowWhatsAppInput(false)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-primary-slate mt-2">
            Enter phone number with country code (no + or spaces)
          </p>
        </div>
      )}

      {/* Email Input */}
      {showEmailInput && (
        <div className="absolute z-10 left-0 right-0 mt-2 p-3 bg-white border border-secondary-lightGray rounded-lg shadow-lg animate-fade-in">
          <div className="flex items-center gap-2">
            <input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleEmailSend}
              disabled={!email.trim() || sending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send
            </button>
            <button
              onClick={() => setShowEmailInput(false)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareOptionsToolbar;
